import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { FaGlobe, FaBullseye, FaUsers, FaTrophy, FaStar, FaMedal, FaAward, FaCheckCircle } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

// HeroSection Component
const HeroSection = () => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start({ opacity: 1, y: 0 });
  }, [controls]);

  return (
    <section className="relative bg-gray-800 text-white py-20">
      <div className="absolute inset-0">
        <img src="/images/hs-about.webp" alt="Hero Background" className="object-cover w-full h-full" />
        <div className="absolute inset-0 bg-black opacity-50" aria-hidden="true"></div>
      </div>
      <div className="relative container mx-auto px-6 text-center">
        <motion.h1
          className="text-5xl font-bold mb-4"
          initial={{ opacity: 0, y: -50 }}
          animate={controls}
          transition={{ duration: 1 }}
        >
          Welcome to Ikusasa Technology Solution
        </motion.h1>
        <motion.p
          className="text-lg mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={controls}
          transition={{ duration: 1, delay: 0.5 }}
        >
          Leading the way in ICT Training and Consulting across the African continent.
        </motion.p>
        <motion.a
          href="#company-overview"
          className="bg-customRed hover:bg-red-600 text-white px-4 py-2 rounded-full border-none cursor-pointer transition duration-300 transform hover:scale-105"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={controls}
          transition={{ duration: 0.5, delay: 1 }}
        >
          Learn More
        </motion.a>
      </div>
    </section>
  );
};

// CompanyOverview Component
const CompanyOverview = ({ title, description, sections }) => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start({ opacity: 1, y: 0 });
  }, [controls]);

  return (
    <section id="company-overview" className="bg-white py-20">
      <div className="container mx-auto px-6 text-center">
        <motion.h2
          className="text-4xl font-extrabold mb-8 text-customBlue"
          initial={{ opacity: 0, y: -50 }}
          animate={controls}
          transition={{ duration: 1 }}
        >
          {title}
        </motion.h2>
        <motion.div
          className="flex flex-col lg:flex-row lg:space-x-8"
          initial={{ opacity: 0 }}
          animate={controls}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <div className="flex-1 mb-8 lg:mb-0">
            {description.map((para, index) => (
              <motion.p
                key={index}
                className="text-lg text-gray-800 leading-relaxed mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={controls}
                transition={{ duration: 0.5, delay: index * 0.3 }}
              >
                {para}
              </motion.p>
            ))}
          </div>
          <div className="flex-1 space-y-6">
            {sections.map(({ id, title, icon: Icon, description }) => (
              <motion.div
                key={id}
                className="flex items-start space-x-4 p-6 bg-blue-100 rounded-lg shadow-lg"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={controls}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <Icon className="w-12 h-12 text-customRed" aria-hidden="true" />
                <div>
                  <h3 className="text-2xl font-semibold mb-2 text-customBlue">{title}</h3>
                  <p className="text-lg text-gray-700">{description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// TeamSection Component
const TeamSection = ({ members }) => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start({ opacity: 1, y: 0 });
  }, [controls]);

  return (
    <section id="team" className="bg-white py-20">
      <div className="container mx-auto px-6 text-center">
        <motion.h2
          className="text-4xl font-extrabold mb-12 text-customBlue"
          initial={{ opacity: 0, y: -50 }}
          animate={controls}
          transition={{ duration: 1 }}
        >
          Our Team
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {members.map(({ id, name, role }) => (
            <motion.div
              key={id}
              className="flex flex-col items-center p-6 bg-white rounded-lg shadow-lg hover:scale-105 transition-transform duration-500"
              initial={{ opacity: 0, y: 50 }}
              animate={controls}
              transition={{ duration: 0.6, delay: id * 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              {/* If you have images, add an img element here */}
              {/* <img src="path_to_image" alt={`${name}`} className="w-24 h-24 rounded-full mb-4" /> */}
              <h3 className="text-xl font-semibold mb-2 text-customRed">{name}</h3>
              <p className="text-lg text-gray-700">{role}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// QCTOAccreditation Component
const QCTOAccreditation = () => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start({ opacity: 1, y: 0 });
  }, [controls]);

  const accreditations = [
    {
      number: "07-QCTO/SDP040925150023",
      validFrom: "29 August 2025",
      validUntil: "28 August 2030",
      courses: [
        { name: "Artificial Intelligence Software Developer", nqf: "5", credits: "209", ofo: "118792" },
      ],
    },
    {
      number: "07-QCTO/SDP180925105324",
      validFrom: "12 September 2025",
      validUntil: "11 September 2030",
      courses: [
        { name: "Cybersecurity Analyst",       nqf: "5", credits: "173", ofo: "118986" },
        { name: "Data Science Practitioner",   nqf: "5", credits: "185", ofo: "118708" },
        { name: "Cloud Administrator",         nqf: "4", credits: "149", ofo: "118699" },
        { name: "Computer Technician",         nqf: "5", credits: "282", ofo: "101408" },
        { name: "Software Engineer",           nqf: "6", credits: "240", ofo: "119458" },
      ],
    },
  ];

  return (
    <section id="accreditation" className="bg-gray-50 py-20">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -30 }}
          animate={controls}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
            <FaAward />
            Officially Accredited
          </div>
          <h2 className="text-4xl font-extrabold text-customBlue mb-4">QCTO Accreditation</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Ikusasa Technology Solutions holds two QCTO accreditations as a Skills Development Provider (SDP),
            covering six nationally recognised occupational qualifications.
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto space-y-8">
          {accreditations.map((acc, i) => (
            <motion.div
              key={acc.number}
              className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              animate={controls}
              transition={{ duration: 0.8, delay: 0.15 * (i + 1) }}
            >
              {/* Header */}
              <div className="bg-customBlue px-8 py-5 flex flex-col sm:flex-row items-center gap-4">
                <div className="flex-shrink-0 bg-white rounded-xl p-2.5">
                  <img src="/images/qcto-2-03.webp" alt="QCTO Logo" className="h-10 w-auto" />
                </div>
                <div className="text-white text-center sm:text-left flex-1">
                  <p className="text-xs font-bold tracking-widest uppercase text-blue-200 mb-0.5">Accreditation Number</p>
                  <p className="font-extrabold text-base">{acc.number}</p>
                </div>
                <div className="text-white text-center sm:text-right text-sm">
                  <p className="text-blue-200 text-xs font-semibold uppercase tracking-wide">Valid</p>
                  <p className="font-bold">{acc.validFrom}</p>
                  <p className="text-blue-200 text-xs">to {acc.validUntil}</p>
                </div>
              </div>

              {/* Courses table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-6 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Qualification</th>
                      <th className="text-center px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">NQF</th>
                      <th className="text-center px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">OFO Code</th>
                      <th className="text-center px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acc.courses.map((c, j) => (
                      <tr key={c.ofo} className={j % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                        <td className="px-6 py-4 font-semibold text-gray-800">Occupational Certificate: {c.name}</td>
                        <td className="px-4 py-4 text-center text-gray-600">Level {c.nqf}</td>
                        <td className="px-4 py-4 text-center text-gray-600">{c.ofo}</td>
                        <td className="px-4 py-4 text-center text-gray-600">{c.credits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="px-8 py-4 bg-green-50 border-t border-gray-100 flex items-center gap-3">
                <FaCheckCircle className="text-green-500 text-base flex-shrink-0" />
                <p className="text-gray-600 text-sm">
                  Accredited for 5 years in accordance with the Skills Development Act (Act No 97 of 1998).
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Component
const About = () => {
  const location = useLocation();
  const companyControls = useAnimation();
  const achievementsControls = useAnimation();
  const teamControls = useAnimation();

  useEffect(() => {
    const path = location.pathname;
    const parts = path.split('/');
    const sectionId = parts.length > 2 ? parts[2] : '';

    if (sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        console.warn(`Element with id "${sectionId}" not found.`);
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    companyControls.start({ opacity: 1, y: 0 });
    achievementsControls.start({ opacity: 1, y: 0 });
    teamControls.start({ opacity: 1, y: 0 });
  }, [companyControls, achievementsControls, teamControls]);

  const companyInfo = {
    title: "About Us",
    description: [
      "Ikusasa Technology Solution is a company specializing in Information, Communication, and Technology (ICT) training and consulting services. Our success is driven by the diverse and specialized services we offer to our clients.",
      "We operate with best practices in General Management, Finance, Human Resources, and Customer Care, providing a wide range of services including ICT hardware and software solutions, applications development, networking, data communication, soft skills training, and ICT certifications.",
      "In collaboration with internationally recognized brands such as CompTIA, IBM, Microsoft, Cisco, Digital Marketing Institute, and MICT Seta, we deliver high-quality, tailored solutions to meet our clients' needs."
    ],
    sections: [
      { id: "vision", title: "Vision", icon: FaGlobe, description: "To be a leading provider for ICT Training and Consulting services across the African continent." },
      { id: "mission", title: "Mission", icon: FaBullseye, description: "To grow every customer's business through our professional team members, enhancing mutual benefits." },
      { id: "empowerment", title: "Empowerment", icon: FaUsers, description: "We strive to advance individuals and foster sustainable community development." }
    ]
  };

  const teamMembers = [
    { id: 1, name: 'Thulisile Dlamini', role: 'Chief Executive Officer (CEO)' },
    { id: 2, name: 'Tshegofatso Tshabalala', role: 'Operations Manager' },
    { id: 3, name: 'Masenelo Mogashoa', role: 'Head of Sales' },
    { id: 4, name: 'Andrew Njoke', role: 'Senior Developer' },
    { id: 5, name: 'Mmathema Legodi', role: 'IT Trainer' },
    { id: 6, name: 'Thabiso Mabiletja', role: 'IT Trainer / Technical Support Specialist' },
    { id: 7, name: 'Lesego Masoba', role: 'Technical Support / Marketing' },
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>About Ikusasa Technology Solutions | Our Vision & Mission</title>
        <meta name="description" content="Learn about Ikusasa Technology Solutions, a leading ICT training and consulting company in Africa. Discover our vision, mission, and dedicated team committed to empowering businesses through technology." />
        <meta name="keywords" content="ICT training, technology consulting, IT solutions, CompTIA, Microsoft training, IBM certification, software development, networking, data communication, soft skills training" />
        <link rel="canonical" href="https://ikusasatech.com/about" />
        <meta property="og:title" content="About Ikusasa Technology Solutions | Our Vision & Mission" />
        <meta property="og:description" content="Learn about Ikusasa Technology Solutions, a leading ICT training and consulting company in Africa. Discover our vision, mission, and dedicated team." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ikusasatech.com/about" />
        <meta property="og:image" content="https://ikusasatech.com/images/hs-about.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Ikusasa Technology Solutions | Our Vision & Mission" />
        <meta name="twitter:description" content="Learn about Ikusasa Technology Solutions, a leading ICT training and consulting company in Africa. Discover our vision, mission, and dedicated team." />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "AboutPage",
              "mainEntity": {
                "@type": "Organization",
                "name": "Ikusasa Technology Solutions",
                "description": "A company specializing in Information, Communication, and Technology (ICT) training and consulting services across Africa.",
                "foundingDate": "2015",
                "founders": [
                  {
                    "@type": "Person",
                    "name": "Thulisile Dlamini",
                    "jobTitle": "Chief Executive Officer"
                  }
                ],
                "employee": [
                  {
                    "@type": "Person",
                    "name": "Tshegofatso Tshabalala",
                    "jobTitle": "Operations Manager"
                  },
                  {
                    "@type": "Person",
                    "name": "Masenelo Mogashoa",
                    "jobTitle": "Head of Sales"
                  },
                  {
                    "@type": "Person",
                    "name": "Andrew Njoke",
                    "jobTitle": "Senior Developer"
                  },
                  {
                    "@type": "Person",
                    "name": "Mmathema Legodi",
                    "jobTitle": "IT Trainer"
                  },
                  {
                    "@type": "Person",
                    "name": "Thabiso Mabiletja",
                    "jobTitle": "IT Trainer / Technical Support Specialist"
                  },
                  {
                    "@type": "Person",
                    "name": "Lesego Masoba",
                    "jobTitle": "Technical Support / Marketing"
                  }
                ],
                "brand": [
                  "CompTIA",
                  "Microsoft",
                  "IBM",
                  "Digital Marketing Institute",
                  "MICT Seta"
                ]
              }
            }
          `}
        </script>
      </Helmet>
      <HeroSection />
      <CompanyOverview
        title={companyInfo.title}
        description={companyInfo.description}
        sections={companyInfo.sections}
      />
      <QCTOAccreditation />
      {/* <AchievementsSection achievements={achievements} controls={achievementsControls} /> */}
      <TeamSection members={teamMembers} />
    </div>
  );
}

export default About;
